import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  FileText, Plus, BookOpen, Globe, Edit2, Trash2, Copy, Search, 
  Grid, List, SortAsc, SortDesc, Filter, Calendar, ChevronRight, CheckCircle, 
  Clock, AlertCircle, Loader, Eye, Download, X, Settings,
  ChevronDown, ChevronUp, Building2, FlaskConical, Lock, Import,
  ArrowDown, ArrowUpRight, MoveRight, ClipboardCheck, Layers
} from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import permissionsService from '../../services/permissionsService';
import api from '../../services/api';
import * as AssessmentBuilderAPI from '../../services/assessmentBuilderApi';
import { APPROVE_ASSESSMENT_PERMISSION } from '../../services/reviewWorkflowApi';
import ApprovalSettingsControl from './ApprovalSettingsControl';
import LibrarySelection from './LibrarySelection';
import ImportConfirmation from './ImportConfirmation';
import ScreenBuilder from './ScreenBuilder';
import CleanAssessmentCard from './CleanAssessmentCard';
import VariantSelectorModal from './VariantSelectorModal';
import PrimaryVariantModal from './PrimaryVariantModal';
import ManageAssessment from './ManageAssessment';
import VPGManagementModal from './VPGManagementModal';
import ArchivedAssessments from './ArchivedAssessments';

// Resolve an assessment's effective status: review_status wins, then
// publication_status, then status, defaulting to draft. Drives the status facets.
const reviewStatusOf = (a) =>
  a?.review_status || a?.publication_status || a?.status || 'draft';

// The at-a-glance summary cards along the top. They double as the status
// filters — clicking one scopes the list to that status (and highlights it),
// so the summary and the filter are the same control. Accent colors match the
// status pills on the cards below.
const STAT_ACCENTS = {
  total:     { icon: Layers,      iconClass: 'text-primary-500', activeClass: 'border-primary-400 dark:border-primary-600 ring-1 ring-primary-400 bg-primary-50/60 dark:bg-primary-900/20', numClass: 'text-primary-700 dark:text-primary-300' },
  draft:     { icon: Clock,       iconClass: 'text-amber-500',   activeClass: 'border-amber-400 dark:border-amber-600 ring-1 ring-amber-400 bg-amber-50/70 dark:bg-amber-900/20',       numClass: 'text-amber-700 dark:text-amber-300' },
  in_review: { icon: Clock,       iconClass: 'text-blue-500',    activeClass: 'border-blue-400 dark:border-blue-600 ring-1 ring-blue-400 bg-blue-50/70 dark:bg-blue-900/20',          numClass: 'text-blue-700 dark:text-blue-300' },
  denied:    { icon: AlertCircle, iconClass: 'text-red-500',     activeClass: 'border-red-400 dark:border-red-600 ring-1 ring-red-400 bg-red-50/70 dark:bg-red-900/20',             numClass: 'text-red-700 dark:text-red-300' },
  approved:  { icon: Globe,       iconClass: 'text-teal-500',    activeClass: 'border-teal-400 dark:border-teal-600 ring-1 ring-teal-400 bg-teal-50/70 dark:bg-teal-900/20',          numClass: 'text-teal-700 dark:text-teal-300' },
  published: { icon: CheckCircle, iconClass: 'text-emerald-500', activeClass: 'border-emerald-400 dark:border-emerald-600 ring-1 ring-emerald-400 bg-emerald-50/70 dark:bg-emerald-900/20', numClass: 'text-emerald-700 dark:text-emerald-300' },
};

const StatCard = ({ accentKey, label, value, hint, active, onClick }) => {
  const a = STAT_ACCENTS[accentKey] || STAT_ACCENTS.total;
  const Icon = a.icon;
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={`text-left rounded-xl border p-4 transition-all ${
        active
          ? `${a.activeClass} shadow-sm`
          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm'
      }`}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        <Icon className={`w-3.5 h-3.5 ${a.iconClass}`} />
        <span className="truncate">{label}</span>
      </div>
      <div className={`mt-1 text-2xl font-extrabold tabular-nums ${active ? a.numClass : 'text-gray-900 dark:text-gray-100'}`}>
        {value}
        {hint && <span className="ml-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 align-middle">{hint}</span>}
      </div>
    </button>
  );
};

// A single filter pill in the facet bar. Active = filled; inactive = outline.
// Same visual whether it filters by status or by library, so the whole bar
// reads as one consistent "click to filter this list" control.
const FacetChip = ({ label, count, active, onClick }) => (
  <button
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${
      active
        ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500'
    }`}
  >
    {label}
    {count != null && (
      <span className={`text-xs font-bold tabular-nums px-1.5 py-0.5 rounded-full ${
        active ? 'bg-white/25 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
      }`}>
        {count}
      </span>
    )}
  </button>
);

// Review states where an assessment has left draft and must NOT be edited in
// place. Opening one forks a new draft version instead, so the completed copy
// (with its pending/signed review and any study references) is preserved.
const LOCKED_EDIT_STATUSES = ['in_review', 'approved', 'published'];

// Human-readable label for the locked-state warning modal.
const LOCKED_STATUS_LABELS = {
  in_review: 'in review',
  approved: 'approved',
  published: 'published',
};

/**
 * Assessment Builder Main - Redesigned with Tiered Library Architecture
 *
 * Hierarchy:
 * 1. VPG Library (Global Templates) - Read-only for all users
 * 2. Sponsor Library (Organization Templates) - Editable by internal users only
 * 3. Study Assessments (Working Documents) - Default view, editable by most users
 * 
 * Import Flow:
 * - VPG → Sponsor: Import global template to sponsor library
 * - Sponsor → Study: Import sponsor template to study
 */
const AssessmentBuilderMain = ({ reviewMode = false, readOnlyView = false }) => {
  const { user } = useContext(AuthContext);
  const { assessmentId: urlAssessmentId } = useParams();
  const navigate = useNavigate();
  // ?study=<id> opens the list pre-scoped to that study's assessments
  // (used by the Study Builder's per-study Assessment Builder link, FECOA-1826)
  const [searchParams] = useSearchParams();
  const urlStudyParam = searchParams.get('study');
  const [hasPermission, setHasPermission] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingFromUrl, setLoadingFromUrl] = useState(!!urlAssessmentId); // True if URL has assessment ID
  const [currentStep, setCurrentStep] = useState('dashboard');
  const [assessmentConfig, setAssessmentConfig] = useState(null);
  
  // Assessment data - separated by library type
  const [assessments, setAssessments] = useState([]);
  const [vpgAssessments, setVpgAssessments] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(true);
  const [loadingVpg, setLoadingVpg] = useState(false);
  
  // Permission states
  const [permissions, setPermissions] = useState({
    canPublishToVpg: false,
    canEditSponsorLibrary: false,
    isInternalUser: false
  });
  
  // UI states
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingAssessmentId, setEditingAssessmentId] = useState(null);
  const [editingAssessmentName, setEditingAssessmentName] = useState('');
  
  // View preferences
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('lastModified');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Section collapse states - Study is expanded by default, others collapsed
  const [sectionsExpanded, setSectionsExpanded] = useState({
    vpg: false,
    sponsor: false,
    study: true,  // Default expanded
    archived: false
  });

  // Active study filter for the Study Assessments section.
  // null = show every study; a study_id restricts to that study's group.
  const [studyFilter, setStudyFilter] = useState(urlStudyParam);

  // Faceted library filter — which library the list is scoped to.
  // 'all' shows every section; otherwise only the matching one renders.
  const [libraryFacet, setLibraryFacet] = useState(urlStudyParam ? 'study' : 'all');
  
  // Trigger to refresh archived section after delete
  const [archivedRefreshTrigger, setArchivedRefreshTrigger] = useState(0);

  // Superuser + selected sponsor — gate/feed the approval-enforcement toggle.
  // Restricted to superusers for now (FECOA-1646).
  const { isSuperuser, selectedSponsorId } = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const sel = JSON.parse(localStorage.getItem('selectedSponsor') || 'null');
      return {
        isSuperuser: !!u?.is_superuser,
        selectedSponsorId: sel?.id ?? u?.sponsor_id ?? null,
      };
    } catch {
      return { isSuperuser: false, selectedSponsorId: null };
    }
  }, []);

  // Whether the current user can approve — gates the Review Queue entry point (FECOA-1613)
  const [canApprove, setCanApprove] = useState(false);
  useEffect(() => {
    let active = true;
    permissionsService
      .hasPermission(APPROVE_ASSESSMENT_PERMISSION)
      .then((ok) => { if (active) setCanApprove(!!ok); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // Open the read-only review view for an in_review assessment (same target as
  // the Review Queue), so approvers can review/decide directly from a card.
  const handleOpenReview = useCallback((entity) => {
    if (entity?.uuid) {
      navigate(`/assessment-builder/review/${entity.uuid}`, { state: { reviewEntity: entity } });
    }
  }, [navigate]);

  // Get sponsor name from localStorage
  const getSponsorName = () => {
    try {
      const storedSponsor = localStorage.getItem('selectedSponsor');
      if (storedSponsor) {
        const sponsor = JSON.parse(storedSponsor);
        return sponsor.name || 'My Sponsor';
      }
    } catch (e) {
      console.error('Error parsing sponsor data:', e);
    }
    return user?.sponsor?.name || 'My Sponsor';
  };
  // Variant modal state
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [selectedAssessmentForVariants, setSelectedAssessmentForVariants] = useState(null);
  
  // Variant creation modal state
  const [variantCreationModalOpen, setVariantCreationModalOpen] = useState(false);
  const [variantCreationContext, setVariantCreationContext] = useState(null);
  
  // Manage assessment modal state
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [selectedAssessmentForManage, setSelectedAssessmentForManage] = useState(null);
  const [shouldAutoFocusRename, setShouldAutoFocusRename] = useState(false);
  
  // VPG Management modal state (superuser only)
  const [vpgManagementModalOpen, setVpgManagementModalOpen] = useState(false);
  const [selectedVpgAssessment, setSelectedVpgAssessment] = useState(null);

  // Import modal state with context (which level importing to)
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importContext, setImportContext] = useState(null); // { source: 'vpg'|'sponsor', target: 'sponsor'|'study', assessment: {...}, study: {...} }

  // Sponsor assessments for import modal
  const [sponsorAssessmentsForImport, setSponsorAssessmentsForImport] = useState([]);
  const [loadingSponsorAssessmentsForImport, setLoadingSponsorAssessmentsForImport] = useState(false);

  // Available studies for copy to study
  const [availableStudies, setAvailableStudies] = useState([]);
  const [loadingStudies, setLoadingStudies] = useState(false);
  
  // VPG Import modal state (legacy - kept for compatibility)
  const [vpgImportModalOpen, setVpgImportModalOpen] = useState(false);

  // Published assessment warning modal (dashboard level)
  const [publishedWarningModalOpen, setPublishedWarningModalOpen] = useState(false);
  const [pendingPublishedAssessment, setPendingPublishedAssessment] = useState(null);
  const [creatingNewVersion, setCreatingNewVersion] = useState(false);
  const [newVersionName, setNewVersionName] = useState('');
  // Tracks which assessment uuid the superuser opened via the read-only
  // bypass. The URL-driven config reload (loadAssessmentFromUrl) reconstructs
  // the config from the API and would otherwise drop the bypass flag, causing
  // the in-builder published warning modal to fire a second time. Stored in a
  // ref so reading it inside the URL effect does not require adding it to the
  // dep array (which would re-trigger the fetch every time the flag changes).
  const bypassedAssessmentUuidRef = useRef(null);

  // Check all permissions on mount
  useEffect(() => {
    const checkAllPermissions = async () => {
      try {
        const [hasAccess, canPublishVpg, canEditSponsor] = await Promise.all([
          permissionsService.hasPermission(permissionsService.PERMISSIONS.ACCESS_ASSESSMENT_BUILDER),
          permissionsService.hasPermission(permissionsService.PERMISSIONS.PUBLISH_TO_VPG_LIBRARY),
          permissionsService.hasPermission(permissionsService.PERMISSIONS.EDIT_SPONSOR_LIBRARY)
        ]);
        
        setHasPermission(hasAccess);
        setPermissions({
          canPublishToVpg: canPublishVpg,
          canEditSponsorLibrary: canEditSponsor || user?.is_internal === true,
          isInternalUser: user?.is_internal === true
        });
      } catch (error) {
        console.error('Error checking permissions:', error);
        setHasPermission(false);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      checkAllPermissions();
    }
  }, [user]);

  // Fetch VPG Library assessments (separate endpoint with library_type filter)
  const fetchVpgAssessments = async ({ silent = false } = {}) => {
    if (!silent) setLoadingVpg(true);
    try {
      const response = await api.get('/api/assessment-builder/assessments/?library_type=vpg');
      const data = response.data;
      const vpgList = data.results || data || [];
      console.debug('🌐 Fetched VPG assessments:', vpgList.length);
      setVpgAssessments(vpgList);
    } catch (error) {
      console.error('Error fetching VPG assessments:', error);
    } finally {
      if (!silent) setLoadingVpg(false);
    }
  };

  const fetchSponsorAssessments = async () => {
    if (!hasPermission) return;

    try {
      const response = await api.get('/api/assessment-builder/assessments/?library_type=sponsor');
      const data = response.data;
      const sponsorList = data.results || data || [];
      console.debug('🏢 Fetched sponsor assessments:', sponsorList.length);
      // Return the list - caller will use it directly
      return sponsorList;
    } catch (error) {
      console.error('Error fetching sponsor assessments:', error);
      return [];
    }
  };

  // `silent` skips the loading spinner so the background lock poll (FECOA-1302)
  // refreshes the "Currently being edited by X" card banners in place without a
  // full-list flash.
  const fetchAssessments = async ({ silent = false } = {}) => {
    if (!hasPermission) return;

    if (!silent) setLoadingAssessments(true);
    try {
      const response = await api.get('/api/assessment-builder/assessments/');
      const data = response.data;
      const assessmentList = data.results || data || [];
      console.debug('📊 Fetched assessments:', assessmentList.length);
      setAssessments(assessmentList);
    } catch (error) {
      console.error('Error fetching assessments:', error);
    } finally {
      if (!silent) setLoadingAssessments(false);
    }
  };

  // Fetch available studies for copy to study
  const fetchStudies = async () => {
    setLoadingStudies(true);
    try {
      const response = await api.get('/api/studies/');
      const data = response.data;
      const studyList = data.results || data || [];
      console.debug('📚 Fetched studies:', studyList.length);
      setAvailableStudies(studyList);
    } catch (error) {
      console.error('Error fetching studies:', error);
    } finally {
      setLoadingStudies(false);
    }
  };

  // Fetch both sponsor/study and VPG assessments on mount
  useEffect(() => {
    if (hasPermission && !loading) {
      fetchAssessments();
      fetchVpgAssessments();
      fetchStudies();
    }
  }, [hasPermission, loading]);

  // Poll the list while the dashboard is visible so the exclusive-edit-lock
  // card banners (FECOA-1302) appear when another user starts editing and clear
  // when they close — no manual refresh. Only runs on the dashboard so we don't
  // fight the editor while it holds/heartbeats its own lock.
  useEffect(() => {
    if (!hasPermission || loading || currentStep !== 'dashboard') return undefined;
    // Refresh immediately on (re)entering the dashboard — returning from an
    // import or the editor must show the new assessment without a reload.
    fetchAssessments({ silent: true });
    fetchVpgAssessments({ silent: true });
    const interval = setInterval(() => {
      fetchAssessments({ silent: true });
      // Keep VPG library cards/counts fresh too — imports and deletes land
      // there and previously only showed after a full page reload.
      fetchVpgAssessments({ silent: true });
    }, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, loading, currentStep]);

  // Auto-load assessment from URL parameter (e.g., /assessment-builder/123)
  useEffect(() => {
    const loadAssessmentFromUrl = async () => {
      // Guard against the literal string "undefined" as a route param, which
      // happens when a caller navigates with an undefined uuid. Without this
      // guard we issue GET /assessments/undefined/ and render an error toast.
      const isMissingId = !urlAssessmentId || urlAssessmentId === 'undefined' || urlAssessmentId === 'null';
      if (isMissingId || !hasPermission || loading) {
        if (isMissingId) {
          setLoadingFromUrl(false);
          if (urlAssessmentId === 'undefined' || urlAssessmentId === 'null') {
            console.warn('[URL] Invalid assessment id in route:', urlAssessmentId, '- redirecting to dashboard');
            navigate('/assessment-builder', { replace: true });
          }
        }
        return;
      }
      
      setLoadingFromUrl(true);
      console.debug('[URL] Loading assessment from URL:', urlAssessmentId);
      try {
        const response = await api.get(`/api/assessment-builder/assessments/${urlAssessmentId}/`);
        const assessmentData = response.data;
        if (assessmentData) {
          const surveyId = assessmentData.survey?.id || assessmentData.survey_id || assessmentData.survey;
          
          const config = {
            ...assessmentData.builder_metadata,
            assessment_id: assessmentData.id,
            assessment_uuid: assessmentData.uuid,
            survey_id: surveyId,
            name: assessmentData.name || assessmentData.survey?.name,
            library_type: assessmentData.library_type,
            assessment_type: assessmentData.assessment_type,
            publication_status: assessmentData.publication_status,
            version: assessmentData.version,
            default_templates: assessmentData.default_templates,
            studies: assessmentData.studies || [],
            mode: 'edit',
            _superuserBypass: bypassedAssessmentUuidRef.current === assessmentData.uuid,
            // readOnlyView (FECOA-1700, AC-5): the /view route opens published
            // assessments in the same hard read-only mode as approver review,
            // minus the review chrome (_viewOnly suppresses the badge).
            _reviewReadOnly: reviewMode || readOnlyView,
            _viewOnly: readOnlyView,
          };
          
          console.debug('[URL] Loaded assessment config:', config);
          setAssessmentConfig(config);
          setCurrentStep('screen-builder');
          setLoadingFromUrl(false);
        } else {
          toast.error('Failed to load assessment');
          setLoadingFromUrl(false);
          navigate('/assessment-builder', { replace: true });
        }
      } catch (error) {
        console.error('[URL] Error loading assessment:', error);
        toast.error('Error loading assessment');
        setLoadingFromUrl(false);
        navigate('/assessment-builder', { replace: true });
      }
    };

    loadAssessmentFromUrl();
  }, [urlAssessmentId, hasPermission, loading, navigate, reviewMode, readOnlyView]);

  // Fetch sponsor assessments when import context changes to 'sponsor'
  useEffect(() => {
    if (importContext && importContext.source === 'sponsor') {
      const loadSponsorAssessments = async () => {
        setLoadingSponsorAssessmentsForImport(true);
        try {
          const list = await fetchSponsorAssessments();
          setSponsorAssessmentsForImport(list);
        } catch (error) {
          console.error('Error loading sponsor assessments for import:', error);
          setSponsorAssessmentsForImport([]);
        } finally {
          setLoadingSponsorAssessmentsForImport(false);
        }
      };
      loadSponsorAssessments();
    }
  }, [importContext?.source]);

  // Helper functions and useMemo - MUST be before all conditional returns
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diff = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    // Handle future dates (timezone mismatch) - treat as "Today"
    if (diff < 0) return 'Today';
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff} days ago`;
    if (diff < 30) {
      const weeks = Math.floor(diff / 7);
      return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
    }
    return date.toLocaleDateString();
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'published':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'review':
      case 'in_review':
        return <Eye className="w-4 h-4 text-blue-500" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-teal-500" />;
      case 'denied':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'draft':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'archived':
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'published':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'review':
      case 'in_review':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'approved':
        return 'bg-teal-50 text-teal-700 border-teal-200';
      case 'denied':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'draft':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'archived':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  // Your status badge function
  const getStatusBadge = (status) => {
    const statusMap = {
      'draft': { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Draft' },
      'review': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Pending Review' },
      'in_review': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Pending Review' },
      'approved': { bg: 'bg-teal-100', text: 'text-teal-800', label: 'Approved' },
      'denied': { bg: 'bg-red-100', text: 'text-red-800', label: 'Changes Requested' },
      'published': { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Published' },
      'archived': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Archived' },
    };
    const style = statusMap[status] || statusMap['draft'];
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  // Helper to filter and sort assessments
  const filterAndSort = (items) => {
    let filtered = items.filter(assessment => {
      const matchesSearch = (assessment.name || assessment.survey?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
      // Match either lens: publication_status (draft/review/published/archived)
      // or review_status (in_review/approved/…), so hub stat-tile filters land.
      const matchesStatus = filterStatus === 'all'
        || assessment.publication_status === filterStatus
        || reviewStatusOf(assessment) === filterStatus;
      const isNotVariant = !assessment.variant_info;
      return matchesSearch && matchesStatus && isNotVariant;
    });

    filtered.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'lastModified') {
        const dateA = new Date(a.builder_metadata?.last_saved || a.modified || 0);
        const dateB = new Date(b.builder_metadata?.last_saved || b.modified || 0);
        comparison = dateB - dateA;
      } else if (sortBy === 'name') {
        const nameA = (a.name || a.survey?.name || '').toLowerCase();
        const nameB = (b.name || b.survey?.name || '').toLowerCase();
        comparison = nameA.localeCompare(nameB);
      }
      return sortOrder === 'asc' ? -comparison : comparison;
    });

    return filtered;
  };

  // Separate assessments by library type for tiered display
  const { studyAssessments, sponsorAssessments } = useMemo(() => {
    const study = filterAndSort(assessments.filter(a => a.library_type === 'study'));
    const sponsor = filterAndSort(assessments.filter(a => a.library_type === 'sponsor'));
    return { studyAssessments: study, sponsorAssessments: sponsor };
  }, [assessments, searchTerm, filterStatus, sortBy, sortOrder]);

  // Group study assessments by their associated study
  const studyAssessmentsGrouped = useMemo(() => {
    const groups = {};
    const ungrouped = [];
    
    studyAssessments.forEach(assessment => {
      if (assessment.studies && assessment.studies.length > 0) {
        // Group by first study (assessment can belong to multiple studies)
        const study = assessment.studies[0];
        const studyKey = study.study_id;
        if (!groups[studyKey]) {
          groups[studyKey] = {
            study_id: study.study_id,
            study_name: study.name,
            assessments: []
          };
        }
        groups[studyKey].assessments.push(assessment);
      } else {
        // No study association
        ungrouped.push(assessment);
      }
    });
    
    // Convert to array sorted by study name
    const grouped = Object.values(groups).sort((a, b) => 
      (a.study_name || '').localeCompare(b.study_name || '')
    );
    
    // Add ungrouped at the end if any
    if (ungrouped.length > 0) {
      grouped.push({
        study_id: null,
        study_name: 'Unassigned',
        assessments: ungrouped
      });
    }
    
    return grouped;
  }, [studyAssessments]);

  // Apply the active study filter (set from the hub's Study Assessments dropdown
  // or the in-section picker). null = show all studies.
  const displayedStudyGroups = useMemo(() => {
    if (studyFilter == null) return studyAssessmentsGrouped;
    if (studyFilter === '__unassigned__') return studyAssessmentsGrouped.filter(g => g.study_id == null);
    return studyAssessmentsGrouped.filter(g => String(g.study_id) === String(studyFilter));
  }, [studyAssessmentsGrouped, studyFilter]);

  const displayedStudyCount = useMemo(
    () => displayedStudyGroups.reduce((sum, g) => sum + g.assessments.length, 0),
    [displayedStudyGroups]
  );

  // VPG assessments filtered and sorted
  const filteredVpgAssessments = useMemo(() => {
    return filterAndSort(vpgAssessments);
  }, [vpgAssessments, searchTerm, filterStatus, sortBy, sortOrder]);

  // At-a-glance counts for the facet bar. These are stable totals over all
  // assessments (not re-filtered as you click), so they read as KPIs.
  const facetCounts = useMemo(() => {
    const live = assessments.filter((a) => !a.variant_info);
    const vpgLive = vpgAssessments.filter((a) => !a.variant_info);
    const all = [...live, ...vpgLive];
    const byStatus = (s) => all.filter((a) => reviewStatusOf(a) === s).length;
    return {
      total: all.length,
      draft: byStatus('draft'),
      inReview: byStatus('in_review'),
      denied: byStatus('denied'),
      approved: byStatus('approved'),
      published: byStatus('published'),
      study: live.filter((a) => a.library_type === 'study').length,
      sponsor: live.filter((a) => a.library_type === 'sponsor').length,
      vpg: vpgLive.length,
    };
  }, [assessments, vpgAssessments]);

  const hasActiveFilter = searchTerm.trim() !== '' || filterStatus !== 'all'
    || libraryFacet !== 'all' || studyFilter != null;

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setLibraryFacet('all');
    setStudyFilter(null);
  };

  // Which library sections render, and whether each is open. When scoped to a
  // single library that section is always shown and forced open (no point
  // collapsing the only thing on screen); in "All" mode the collapse toggles win.
  const showSection = {
    study: libraryFacet === 'all' || libraryFacet === 'study',
    sponsor: libraryFacet === 'all' || libraryFacet === 'sponsor',
    vpg: libraryFacet === 'all' || libraryFacet === 'vpg',
    archived: libraryFacet === 'all' || libraryFacet === 'archived',
  };
  const sectionOpen = {
    study: libraryFacet === 'study' || (libraryFacet === 'all' && sectionsExpanded.study),
    sponsor: libraryFacet === 'sponsor' || (libraryFacet === 'all' && sectionsExpanded.sponsor),
    vpg: libraryFacet === 'vpg' || (libraryFacet === 'all' && sectionsExpanded.vpg),
    archived: libraryFacet === 'archived' || (libraryFacet === 'all' && sectionsExpanded.archived),
  };

  if (loading || loadingFromUrl) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-full border-4 border-gray-200 dark:border-gray-700"></div>
          <div className="absolute inset-0 rounded-full border-4 border-primary-600 border-t-transparent animate-spin"></div>
        </div>
        <p className="mt-4 text-gray-600 font-medium">
          {loadingFromUrl ? 'Loading assessment...' : 'Loading Assessment Builder...'}
        </p>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 px-4">
        <div className="relative w-24 h-24 mb-6">
          <div className="absolute inset-0 bg-red-100 rounded-full opacity-30"></div>
          <div className="absolute inset-4 bg-red-200 rounded-full opacity-30"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <AlertCircle className="w-12 h-12 text-red-600" />
          </div>
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-3 text-center">Access Denied</h2>
        <p className="text-gray-600 text-center max-w-md">You don't have permission to access the Assessment Builder.</p>
        <p className="text-sm text-gray-500 text-center">Contact your administrator to request access.</p>
      </div>
    );
  }

  const handleCreateNewAssessment = () => {
    setCurrentStep('library-selection');
  };

  const handleContinueEditing = async (assessment) => {
    console.log('Continue editing assessment:', assessment);
    
    let assessmentData = assessment;
    let surveyId = assessment.survey?.id || assessment.survey_id || assessment.survey;
    
    // If survey_id is missing, fetch the full assessment details
    if (!surveyId && (assessment.uuid || assessment.id)) {
      console.log('survey_id missing, fetching full assessment details...');
      try {
        const response = await api.get(`/api/assessment-builder/assessments/${assessment.uuid || assessment.id}/`);
        assessmentData = response.data;
        surveyId = assessmentData.survey?.id || assessmentData.survey_id || assessmentData.survey;
        console.log('Fetched assessment details, survey_id:', surveyId);
      } catch (error) {
        console.error('Error fetching assessment details:', error);
      }
    }

    const config = {
      ...assessmentData.builder_metadata,
      assessment_id: assessmentData.id,
      assessment_uuid: assessmentData.uuid,
      survey_id: surveyId,
      name: assessmentData.name || assessmentData.survey?.name,
      library_type: assessmentData.library_type || assessment.library_type,
      assessment_type: assessmentData.assessment_type || assessment.assessment_type,
      publication_status: assessmentData.publication_status || assessment.publication_status,
      version: assessmentData.version || assessment.version,
      studies: assessmentData.studies || [],
      mode: 'edit'
    };
    
    console.log('Parsed config:', config);
    
    if (!config.survey_id) {
      console.error('Unable to determine survey_id from assessment:', assessmentData);
      toast.error('Error: Unable to determine survey ID. Please try refreshing the page.');
      return;
    }
    
    // Check if assessment has left draft (in_review/approved/published) - it is
    // locked, so show the warning modal that forks a new draft version instead
    // of editing the completed copy in place.
    if (LOCKED_EDIT_STATUSES.includes(config.publication_status)) {
      console.log(`Locked assessment detected (${config.publication_status}), showing new-version modal`);
      setPendingPublishedAssessment({ config, assessmentData });
      setPublishedWarningModalOpen(true);
      return;
    }
    
    setAssessmentConfig(config);
    setCurrentStep('screen-builder');
    navigate(`/assessment-builder/${config.assessment_uuid}`, { replace: true });
  };

  const handleCreateNewVersionFromDashboard = async () => {
    if (!pendingPublishedAssessment) return;
    
    setCreatingNewVersion(true);
    try {
      const response = await api.post(
        `/api/assessment-builder/assessments/${pendingPublishedAssessment.config.assessment_uuid}/create-new-version/`,
        { name: newVersionName.trim() || undefined }
      );

      const data = response.data;
      toast.success(`Created new draft version ${data.version}`);

      // Create new config for the draft version
      const newConfig = {
        assessment_id: data.new_assessment_id,
        assessment_uuid: data.new_assessment_uuid,
        survey_id: data.new_survey_id,
        name: data.name,
        library_type: pendingPublishedAssessment.config.library_type,
        assessment_type: pendingPublishedAssessment.config.assessment_type,
        publication_status: 'draft',
        version: data.version,
        studies: pendingPublishedAssessment.config.studies || [],
        mode: 'edit'
      };

      // Close modal and navigate to new draft
      setPublishedWarningModalOpen(false);
      setPendingPublishedAssessment(null);
      setNewVersionName('');
      setAssessmentConfig(newConfig);
      setCurrentStep('screen-builder');
      navigate(`/assessment-builder/${newConfig.assessment_uuid}`, { replace: true });
      
      // Refresh assessment list
      fetchAssessments();
    } catch (error) {
      console.error('Error creating new version:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to create new version';
      toast.error(errorMsg);
    } finally {
      setCreatingNewVersion(false);
    }
  };

  // FECOA-1700 (AC-5): open a published assessment read-only without forking a
  // draft. The /view route mounts this component with readOnlyView, so the URL
  // loader applies _reviewReadOnly and the mode survives a hard refresh.
  const handleViewReadOnly = (assessment) => {
    navigate(`/assessment-builder/view/${assessment.uuid || assessment.id}`);
  };

  const handlePreviewAssessment = (assessment) => {
    // For VPG library items, show management modal
    if (assessment.library_type === 'vpg') {
      setSelectedVpgAssessment(assessment);
      setVpgManagementModalOpen(true);
    }
  };

  // Superuser-only escape hatch: open a published assessment without forcing a
  // new draft version. Lands in the screen builder in read-only mode; the
  // builder shows a lock banner with a toggle if edits are needed.
  const handleOpenReadOnlyBypass = () => {
    if (!pendingPublishedAssessment) return;
    const config = {
      ...pendingPublishedAssessment.config,
      _superuserBypass: true,
    };
    bypassedAssessmentUuidRef.current = config.assessment_uuid;
    setPublishedWarningModalOpen(false);
    setPendingPublishedAssessment(null);
    setNewVersionName('');
    setAssessmentConfig(config);
    setCurrentStep('screen-builder');
    navigate(`/assessment-builder/${config.assessment_uuid}`, { replace: true });
  };

  const handleLibrarySelectionNext = async (config) => {
    // If mode is 'import' or 'edit' (copy), IDs already exist
    if (config.mode === 'import' && config.assessment_id) {
      setAssessmentConfig(config);
      setCurrentStep('import-confirmation');
      return;
    }
    
    if (config.mode === 'edit' && config.assessment_id) {
      setAssessmentConfig(config);
      setCurrentStep('screen-builder');
      navigate(`/assessment-builder/${config.assessment_uuid}`, { replace: true });
      return;
    }
    
    // For NEW manual creation, create the assessment first
    try {
      setLoadingAssessments(true);
      const payload = {
        survey_data: {
          name: config.name,
          language: 'en-US',
          description: config.description || '',
          long_description: ''
        },
        library_type: config.library_type,
        assessment_type: config.assessment_type,
        therapeutic_area: config.therapeutic_area || '',
        version: config.version || '1.0',
        builder_metadata: {}
      };
      
      if (config.study_id) {
        payload.study_id = config.study_id;
      }
      
      console.log('Creating new assessment:', payload);
      
      const response = await api.post('/api/assessment-builder/assessments/', payload);
      const newAssessment = response.data;
      console.log('Created assessment:', newAssessment);
      
      // Now set config with the actual IDs from backend
      setAssessmentConfig({
        ...config,
        assessment_id: newAssessment.id,
        assessment_uuid: newAssessment.uuid,
        survey_id: newAssessment.survey_id || newAssessment.survey?.id,
        studies: newAssessment.studies || config.studies || [],
        mode: 'edit'  // Switch to edit mode now that it exists
      });

      // Refresh assessment list
      fetchAssessments();

      setCurrentStep('screen-builder');
      navigate(`/assessment-builder/${newAssessment.uuid}`, { replace: true });
    } catch (error) {
      console.error('Error creating assessment:', error);
      console.error('Error response data:', error.response?.data);
      
      // Extract field-specific errors (like duplicate name)
      const errorData = error.response?.data;
      
      // Check for name field error - DRF returns as {'name': ['error message']} or {'name': 'error message'}
      const nameError = errorData?.name || errorData?.survey_data?.name;
      
      if (nameError) {
        const errorMessage = Array.isArray(nameError) ? nameError[0] : nameError;
        console.log('Setting field error for name:', errorMessage);
        // Pass field error back to LibrarySelection via config
        setAssessmentConfig({
          ...config,
          fieldErrors: { name: errorMessage }
        });
        // Stay on library-selection step (don't navigate away)
        return;
      } else {
        // Generic error toast for non-field errors
        const errorMsg = errorData?.error || errorData?.detail || 
          (typeof errorData === 'string' ? errorData : error.message);
        toast.error(`Failed to create assessment: ${errorMsg}`);
      }
    } finally {
      setLoadingAssessments(false);
    }
  };

  const handleImportConfirmationContinue = (importData) => {
    if (!importData?.assessment_uuid) {
      console.error('Import completed without assessment_uuid:', importData);
      toast.error('Import completed, but the assessment UUID was missing. Please refresh the assessment list and open it from the dashboard.');
      fetchAssessments();
      setCurrentStep('dashboard');
      navigate('/assessment-builder', { replace: true });
      return;
    }

    setAssessmentConfig({
      ...assessmentConfig,
      assessment_id: importData.assessment_id,
      assessment_uuid: importData.assessment_uuid,
      survey_id: importData.survey_id,
      name: importData.name,
      mode: 'import'
    });
    setCurrentStep('screen-builder');
    navigate(`/assessment-builder/${importData.assessment_uuid}`, { replace: true });
  };

  const handleBackToLibrarySelection = () => {
    setCurrentStep('library-selection');
  };

  const handleBackToConfirmation = () => {
    setCurrentStep('import-confirmation');
    fetchAssessments();
  };

  const handleBackToDashboard = () => {
    setCurrentStep('dashboard');
    setAssessmentConfig(null);
    navigate('/assessment-builder', { replace: true });
    fetchAssessments();
  };

  const handleSaveScreens = async (screens, items = [], options = {}) => {
    try {
      const assessmentId = assessmentConfig?.assessment_uuid;
      const surveyId = assessmentConfig?.survey_id;

      if (!assessmentId || !surveyId) {
        console.error('Missing IDs - assessmentConfig:', assessmentConfig);
        throw new Error('Missing assessment or survey ID');
      }

      console.debug('Saving screens for survey:', surveyId, 'Assessment:', assessmentId);
      console.debug('[SAVE] Screens data being saved:', JSON.stringify(screens, null, 2));
      console.debug('[SAVE] Items to persist:', items.length);
      console.debug('[SAVE] Device mode:', options.device_mode);

      // Use API service with automatic token refresh, include sidebar items and device settings
      const data = await AssessmentBuilderAPI.saveScreens(assessmentId, surveyId, screens, items, {
        device_mode: options.device_mode,
        target_device_id: options.target_device_id
      });
      console.debug('Screens saved successfully:', data);
      return data;
    } catch (error) {
      console.error('Error saving screens:', error);
      throw error;
    }
  };

  const handleDeleteAssessment = async (assessment) => {
    setDeleting(true);
    try {
      await api.delete(`/api/assessment-builder/assessments/${assessment.uuid}/`);
      setAssessments(assessments.filter(a => a.id !== assessment.id));
      // Also update VPG assessments if this was a VPG library item
      if (assessment.library_type === 'vpg') {
        setVpgAssessments(vpgAssessments.filter(a => a.id !== assessment.id));
      }
      setDeleteConfirmation(null);
      // Also close the manage modal if it's open
      setManageModalOpen(false);
      setSelectedAssessmentForManage(null);
      // Trigger refresh of archived section
      setArchivedRefreshTrigger(prev => prev + 1);
      toast.success('Assessment archived successfully');
    } catch (error) {
      console.error('Error deleting assessment:', error);
      const data = error.response?.data;
      const errorMsg = (typeof data === 'string' ? data : null)
        || data?.detail
        || data?.error
        || (Array.isArray(data) ? data[0] : null)
        || 'Failed to delete assessment';
      toast.error(errorMsg);
    } finally {
      setDeleting(false);
    }
  };

  const handleDuplicateAssessment = async (assessment) => {
    try {
      const response = await api.post(`/api/assessment-builder/assessments/${assessment.uuid}/duplicate/`);
      const duplicated = response.data;
      setAssessments([duplicated, ...assessments]);
      toast.success('Assessment duplicated!');
    } catch (error) {
      console.error('Error duplicating assessment:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to duplicate assessment';
      toast.error(errorMsg);
    }
  };

  const handleImportAssessment = async (assessment, targetLibraryType, studyId = null, customName = null) => {
    try {
      // Verify sponsor context exists before copying to sponsor library
      if (targetLibraryType === 'sponsor') {
        const selectedSponsor = localStorage.getItem('selectedSponsor');
        if (!selectedSponsor) {
          console.error('[ASSESSMENT_BUILDER] No sponsor context found for sponsor library copy');
          toast.error('Please select a sponsor first. Redirecting to sponsor selection...');
          setTimeout(() => navigate('/sponsor-selector'), 1500);
          return { success: false, error: 'No sponsor context. Please select a sponsor and try again.' };
        }
      }
      
      // Build payload
      const payload = { 
        library_type: targetLibraryType,
        name: customName || `${assessment.name} (Copy)`
      };
      
      // Add study_id if copying to study
      if (targetLibraryType === 'study' && studyId) {
        payload.study_id = studyId;
      }
      
      // Duplicate the assessment and change library_type to target
      const response = await api.post(`/api/assessment-builder/assessments/${assessment.uuid}/duplicate/`, payload);
      const imported = response.data;
      setAssessments([imported, ...assessments]);
      
      const targetLabel = targetLibraryType === 'sponsor' ? getSponsorName() + ' Library' : 'Study';
      toast.success(`Assessment copied to ${targetLabel}!`);
      
      // Refresh assessments to show the new one in the correct section
      fetchAssessments();
      
      // Open manage modal for the imported assessment
      setSelectedAssessmentForManage(imported);
      setManageModalOpen(true);
      
      return { success: true };
    } catch (error) {
      console.error('Error copying assessment:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to copy assessment';
      return { success: false, error: errorMsg };
    }
  };

  const startEditingAssessmentName = (assessmentId, currentName) => {
    setEditingAssessmentId(assessmentId);
    setEditingAssessmentName(currentName);
  };

  const saveAssessmentName = async (assessmentId) => {
    if (!editingAssessmentName.trim()) {
      cancelEditingAssessmentName();
      return;
    }

    try {
      await api.patch(`/api/assessment-builder/assessments/${assessmentId}/`, { name: editingAssessmentName.trim() });
      setAssessments(assessments.map(a =>
        a.uuid === assessmentId ? { ...a, name: editingAssessmentName.trim() } : a
      ));
    } catch (error) {
      console.error('Error renaming assessment:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to rename assessment';
      toast.error(errorMsg);
    } finally {
      setEditingAssessmentId(null);
      setEditingAssessmentName('');
    }
  };

  const cancelEditingAssessmentName = () => {
    setEditingAssessmentId(null);
    setEditingAssessmentName('');
  };

  // Variant handlers
  const handleOpenVariantModal = (assessment) => {
    setSelectedAssessmentForVariants(assessment);
    setVariantModalOpen(true);
  };

  const handleCloseVariantModal = () => {
    setVariantModalOpen(false);
    setSelectedAssessmentForVariants(null);
  };

  const handleSelectVariant = (variant) => {
    // Navigate to the selected variant for editing
    handleContinueEditing(variant);
  };

  const handleCreateVariant = (assessment) => {
    // Open variant creation modal with pre-selected primary assessment
    // Get current device from assessment config if available (for pre-selection)
    const currentDeviceId = assessmentConfig?.selectedDevice || 
                            assessmentConfig?.builder_metadata?.current_device_id;
    
    setVariantCreationContext({
      primaryAssessment: assessment,
      preselectedDeviceId: currentDeviceId
    });
    setVariantCreationModalOpen(true);
  };
  
  const handleCreateVariantSubmit = async (variantForm) => {
    try {
      const response = await api.post(
        `/api/assessment-builder/assessments/${variantForm.primary_assessment_uuid}/create-variant/`,
        {
          device_id: parseInt(variantForm.device_id),
          language: variantForm.language,
          copy_structure: variantForm.copy_structure,
          variant_name: variantForm.variant_name || undefined
        }
      );
      
      const data = response.data;
      
      // Refresh assessments to show new variant
      await fetchAssessments();
      
      // Close modal
      setVariantCreationModalOpen(false);
      setVariantCreationContext(null);
      
      // Optionally open the new variant in Screen Builder
      if (data.variant_assessment_uuid) {
        handleContinueEditing({ id: data.variant_assessment, uuid: data.variant_assessment_uuid });
      }
    } catch (error) {
      console.error('Error creating variant:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to create variant';
      throw new Error(errorMsg); // Let modal handle the error display
    }
  };

  // Manage assessment handlers
  const handleOpenManageModal = (assessment) => {
    // Open modal immediately with list data - lazy load details per tab
    setSelectedAssessmentForManage(assessment);
    setManageModalOpen(true);
  };

  const handleCloseManageModal = () => {
    setManageModalOpen(false);
    setSelectedAssessmentForManage(null);
    setShouldAutoFocusRename(false);
  };

  const handleCreateNewVersion = async (assessment) => {
    try {
      const response = await api.post(`/api/assessment-builder/assessments/${assessment.uuid}/create_version/`);
      const newVersion = response.data;
      setAssessments([newVersion, ...assessments]);
      handleContinueEditing(newVersion);
    } catch (error) {
      console.error('Error creating new version:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to create new version';
      toast.error(errorMsg);
    }
  };

  const quickActions = [
    {
      title: 'Create New Assessment',
      description: 'Build a new clinical outcome assessment from scratch',
      icon: Plus,
      color: 'bg-primary-500 hover:bg-primary-600',
      action: handleCreateNewAssessment,
    },
    {
      title: 'VPG Library',
      description: 'Access global assessment library with validated instruments',
      icon: Globe,
      color: 'bg-blue-500 hover:bg-blue-600',
      action: () => console.log('VPG Library'),
    },
    {
      title: 'Assessment Library',
      description: 'Browse and manage your organization\'s assessment library',
      icon: BookOpen,
      color: 'bg-purple-500 hover:bg-purple-600',
      action: () => console.log('Assessment Library'),
    },
    {
      title: 'Builder Settings',
      description: 'Configure assessment builder preferences and templates',
      icon: Settings,
      color: 'bg-gray-500 hover:bg-gray-600',
      action: () => console.log('Settings'),
    }
  ];

  // Render different steps
  if (currentStep === 'library-selection') {
    return (
      <LibrarySelection
        onNext={handleLibrarySelectionNext}
        onBack={handleBackToDashboard}
        fieldErrors={assessmentConfig?.fieldErrors}
        onClearFieldError={(field) => setAssessmentConfig(prev => ({
          ...prev,
          fieldErrors: { ...prev?.fieldErrors, [field]: null }
        }))}
      />
    );
  }

  if (currentStep === 'import-confirmation') {
    return (
      <ImportConfirmation
        importData={assessmentConfig}
        onContinue={handleImportConfirmationContinue}
        onBack={handleBackToLibrarySelection}
        onBackToDashboard={handleBackToDashboard}
      />
    );
  }

  if (currentStep === 'screen-builder') {
    return (
      <ScreenBuilder
        assessmentConfig={assessmentConfig}
        onBack={handleBackToDashboard}
        onSave={handleSaveScreens}
        onAssessmentChange={(newConfig) => {
          console.log('[AssessmentBuilderMain] Assessment changed to:', newConfig);
          setAssessmentConfig(newConfig);
        }}
      />
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 10, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 30
      }
    }
  };

  const AssessmentCard = ({ assessment }) => (
    <motion.div
      variants={itemVariants}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 overflow-hidden transition-shadow duration-300 hover:shadow-md cursor-pointer group"
      onClick={() => handleContinueEditing(assessment)}
    >
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="bg-primary-50 p-2.5 rounded-full">
            <FileText className="w-5 h-5 text-primary-600" />
          </div>
          <div className={`text-xs font-medium px-2.5 py-1 rounded-full border flex items-center space-x-1 ${getStatusColor(assessment.survey?.status || assessment.status)}`}>
            {getStatusIcon(assessment.survey?.status || assessment.status)}
            <span className="capitalize">{(assessment.survey?.status || assessment.status)}</span>
          </div>
        </div>
        
        {editingAssessmentId === assessment.uuid ? (
          <input
            type="text"
            value={editingAssessmentName}
            onChange={(e) => setEditingAssessmentName(e.target.value)}
            onBlur={() => saveAssessmentName(assessment.uuid)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveAssessmentName(assessment.uuid);
              if (e.key === 'Escape') cancelEditingAssessmentName();
            }}
            onClick={(e) => e.stopPropagation()}
            autoFocus
            className="text-lg font-semibold px-2 py-1 border border-primary-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 w-full mb-3"
          />
        ) : (
          <h3 
            className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors truncate mb-3"
            onDoubleClick={(e) => {
              e.stopPropagation();
              startEditingAssessmentName(assessment.uuid, assessment.name || assessment.survey?.name || 'Untitled Assessment');
            }}
            title={assessment.name || assessment.survey?.name || 'Untitled Assessment'}
          >
            {assessment.name || assessment.survey?.name || 'Untitled Assessment'}
          </h3>
        )}
        
        <div className="flex items-center gap-2 mb-4 text-xs text-gray-500">
          <span>v{assessment.version || '1.0'}</span>
          <span>•</span>
          <span>{assessment.assessment_type === 'fixed_form' ? 'Fixed Form' : 'Item Bank'}</span>
        </div>
        
        <div className="flex items-center text-xs text-gray-500 mb-4">
          <Calendar className="w-3.5 h-3.5 mr-1.5" />
          <span>{formatDate(assessment.builder_metadata?.last_saved || assessment.modified)}</span>
        </div>
        
        <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
          <span className="text-sm font-medium text-primary-600">
            {(assessment.survey?.status === 'draft' || assessment.status === 'draft') ? 'Continue Editing' : 'View'}
          </span>
          <div className="flex items-center space-x-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicateAssessment(assessment);
              }}
              className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors rounded"
              title="Duplicate"
            >
              <Copy className="w-4 h-4" />
            </button>
            {(assessment.survey?.status === 'draft' || assessment.status === 'draft') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirmation(assessment);
                }}
                className="p-1.5 text-gray-400 hover:text-red-600 transition-colors rounded"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );

  const AssessmentListItem = ({ assessment }) => (
    <motion.div
      variants={itemVariants}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 overflow-hidden transition-shadow duration-300 hover:shadow-md cursor-pointer group"
      onClick={() => handleContinueEditing(assessment)}
    >
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center flex-1 min-w-0 gap-4">
          <div className="bg-primary-50 p-2.5 rounded-full flex-shrink-0">
            <FileText className="w-5 h-5 text-primary-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-gray-900 group-hover:text-primary-600 transition-colors truncate mb-1">
              {assessment.name || assessment.survey?.name || 'Untitled Assessment'}
            </h3>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span>v{assessment.version || '1.0'}</span>
              <span>•</span>
              <span>{assessment.assessment_type === 'fixed_form' ? 'Fixed Form' : 'Item Bank'}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className={`text-xs font-medium px-2.5 py-1 rounded-full border flex items-center space-x-1 ${getStatusColor(assessment.survey?.status || assessment.status)}`}>
            {getStatusIcon(assessment.survey?.status || assessment.status)}
            <span className="capitalize">{(assessment.survey?.status || assessment.status)}</span>
          </div>
          <div className="text-xs text-gray-500 flex items-center min-w-[100px]">
            <Calendar className="w-3.5 h-3.5 mr-1.5" />
            <span>{formatDate(assessment.builder_metadata?.last_saved || assessment.modified)}</span>
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicateAssessment(assessment);
              }}
              className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors rounded"
              title="Duplicate"
            >
              <Copy className="w-4 h-4" />
            </button>
            {(assessment.survey?.status === 'draft' || assessment.status === 'draft') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirmation(assessment);
                }}
                className="p-1.5 text-gray-400 hover:text-red-600 transition-colors rounded"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <ChevronRight className="w-5 h-5 text-primary-600 group-hover:translate-x-1 transition-transform duration-300" />
          </div>
        </div>
      </div>
    </motion.div>
  );


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-[1600px] mx-auto">
        <motion.div
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-5 bg-primary-600 flex items-center justify-between">
            <div className="flex items-center">
              <FileText className="w-6 h-6 text-white mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-white leading-tight">Assessment Builder</h2>
                <p className="text-sm text-white/80 mt-0.5">
                  {getSponsorName()} · {facetCounts.total} assessment{facetCounts.total === 1 ? '' : 's'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setImportModalOpen(true)}
                className="inline-flex items-center px-4 py-2 bg-primary-500 text-white font-medium rounded-lg border border-primary-400 hover:bg-primary-400 transition-all duration-200"
              >
                <Download className="w-5 h-5 mr-2" />
                Copy from Library
              </button>
              <button
                onClick={handleCreateNewAssessment}
                className="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-700 text-primary-700 dark:text-primary-300 font-semibold rounded-lg border-2 border-white dark:border-gray-600 shadow-md dark:shadow-gray-900/20 hover:bg-primary-50 dark:hover:bg-gray-600 hover:scale-105 transition-all duration-200"
              >
                <Plus className="w-5 h-5 mr-2" />
                New Assessment
              </button>
            </div>
          </div>

          {/* Content - Tiered Library Architecture */}
          <div className="p-6 space-y-6">
            {loadingAssessments ? (
              <div className="px-6 py-16 flex flex-col items-center justify-center">
                <Loader className="w-10 h-10 text-primary-600 animate-spin mb-4" />
                <p className="text-gray-500">Loading assessments...</p>
              </div>
            ) : (
              <>
                {/* Summary hub — KPI cards double as status filters; the list
                    below updates in place, nothing navigates. */}
                <div className="space-y-4 pb-5 border-b border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <StatCard accentKey="total" label="Total" value={facetCounts.total} active={filterStatus === 'all'} onClick={() => setFilterStatus('all')} />
                    <StatCard accentKey="draft" label="Draft" value={facetCounts.draft} active={filterStatus === 'draft'} onClick={() => setFilterStatus(filterStatus === 'draft' ? 'all' : 'draft')} />
                    <StatCard accentKey="in_review" label="Pending review" value={facetCounts.inReview} hint="awaiting" active={filterStatus === 'in_review'} onClick={() => setFilterStatus(filterStatus === 'in_review' ? 'all' : 'in_review')} />
                    <StatCard accentKey="denied" label="Changes req." value={facetCounts.denied} active={filterStatus === 'denied'} onClick={() => setFilterStatus(filterStatus === 'denied' ? 'all' : 'denied')} />
                    <StatCard accentKey="approved" label="Ready to publish" value={facetCounts.approved} active={filterStatus === 'approved'} onClick={() => setFilterStatus(filterStatus === 'approved' ? 'all' : 'approved')} />
                    <StatCard accentKey="published" label="Published" value={facetCounts.published} active={filterStatus === 'published'} onClick={() => setFilterStatus(filterStatus === 'published' ? 'all' : 'published')} />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500 w-16">Library</span>
                    <FacetChip label="All" active={libraryFacet === 'all'} onClick={() => setLibraryFacet('all')} />
                    <FacetChip label="Study" count={facetCounts.study} active={libraryFacet === 'study'} onClick={() => setLibraryFacet(libraryFacet === 'study' ? 'all' : 'study')} />
                    <FacetChip label={getSponsorName()} count={facetCounts.sponsor} active={libraryFacet === 'sponsor'} onClick={() => setLibraryFacet(libraryFacet === 'sponsor' ? 'all' : 'sponsor')} />
                    <FacetChip label="VPG" count={facetCounts.vpg} active={libraryFacet === 'vpg'} onClick={() => setLibraryFacet(libraryFacet === 'vpg' ? 'all' : 'vpg')} />
                    <FacetChip label="Archived" active={libraryFacet === 'archived'} onClick={() => setLibraryFacet(libraryFacet === 'archived' ? 'all' : 'archived')} />
                    {hasActiveFilter && (
                      <button
                        onClick={clearAllFilters}
                        className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                      >
                        <X className="w-4 h-4" /> Clear filters
                      </button>
                    )}
                  </div>
                </div>

                {/* Search and Controls - Always visible */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="relative flex-1 max-w-md">
                    <input
                      type="text"
                      placeholder="Search all assessments..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {/* Sort — choose field, then toggle direction */}
                    <div className="flex items-center rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
                      <label className="sr-only" htmlFor="ab-sort-field">Sort by</label>
                      <select
                        id="ab-sort-field"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="text-sm pl-3 pr-2 py-2.5 bg-transparent text-gray-700 dark:text-gray-200 focus:outline-none cursor-pointer"
                      >
                        <option value="lastModified">Last modified</option>
                        <option value="name">Name</option>
                      </select>
                      <button
                        onClick={() => setSortOrder(order => order === 'asc' ? 'desc' : 'asc')}
                        className="p-2.5 border-l border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        title={sortOrder === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
                      >
                        {sortOrder === 'asc' ? <SortAsc size={18} /> : <SortDesc size={18} />}
                      </button>
                    </div>

                    {/* Approval enforcement — superusers only for now (FECOA-1646) */}
                    {isSuperuser && (
                      <ApprovalSettingsControl sponsorId={selectedSponsorId} />
                    )}

                    {/* Review Queue — approvers only */}
                    {canApprove && (
                      <button
                        onClick={() => navigate('/assessment-builder/review-queue')}
                        className="p-2.5 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors flex items-center gap-1.5"
                        title="Review queue"
                      >
                        <ClipboardCheck size={18} />
                        <span className="text-sm font-medium hidden sm:inline">Review Queue</span>
                      </button>
                    )}

                    {/* View Toggle */}
                    <div className="flex bg-white dark:bg-gray-800 border border-gray-300 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`p-2.5 transition-colors ${viewMode === 'grid' ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      >
                        <Grid size={18} />
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`p-2.5 transition-colors ${viewMode === 'list' ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      >
                        <List size={18} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* No-results State - shown when an active search/filter returns nothing */}
                {!loadingAssessments && hasActiveFilter && studyAssessments.length === 0 && sponsorAssessments.length === 0 && filteredVpgAssessments.length === 0 && (
                  <div className="text-center py-12 max-w-2xl mx-auto">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-gray-100 dark:bg-gray-800 text-gray-400 rounded-full mb-4">
                      <Search className="w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">No assessments match your search</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                      {searchTerm.trim() && (
                        <>No results for <span className="font-medium text-gray-800 dark:text-gray-200">"{searchTerm}"</span>{filterStatus !== 'all' ? ` with status "${filterStatus}"` : ''}.</>
                      )}
                      {!searchTerm.trim() && filterStatus !== 'all' && (
                        <>No assessments with status <span className="font-medium text-gray-800 dark:text-gray-200">"{filterStatus}"</span>.</>
                      )}
                    </p>
                    <div className="flex items-center justify-center gap-3">
                      {searchTerm.trim() && (
                        <button
                          onClick={() => setSearchTerm('')}
                          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          Clear search
                        </button>
                      )}
                      {filterStatus !== 'all' && (
                        <button
                          onClick={() => setFilterStatus('all')}
                          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          Reset status filter
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Empty State - Welcome Message (shown only when there are truly no assessments and no active filter) */}
                {!loadingAssessments && !hasActiveFilter && assessments.length === 0 && vpgAssessments.length === 0 && (
                  <div className="text-center py-12 max-w-4xl mx-auto">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 text-primary-600 rounded-full mb-6">
                      <FileText className="w-8 h-8" />
                    </div>
                    
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to Assessment Builder</h2>
                    <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
                      Create and manage clinical outcome assessments with ease
                    </p>
                    
                    {/* Action Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow duration-300">
                        <div className="flex flex-col h-full">
                          <div className="bg-primary-50 text-primary-600 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                            <Plus className="w-6 h-6" />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">Create New Assessment</h3>
                          <p className="text-gray-600 mb-6 flex-grow">Build a new clinical outcome assessment from scratch</p>
                          <button
                            onClick={handleCreateNewAssessment}
                            className="mt-auto w-full py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors duration-300 flex items-center justify-center"
                          >
                            <Plus className="w-5 h-5 mr-2" />
                            Create Assessment
                          </button>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow duration-300">
                        <div className="flex flex-col h-full">
                          <div className="bg-primary-50 text-primary-600 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                            <Globe className="w-6 h-6" />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">VPG Library</h3>
                          <p className="text-gray-600 mb-6 flex-grow">Access validated instruments from the global library</p>
                          <button
                            onClick={() => setSectionsExpanded(prev => ({ ...prev, vpg: true }))}
                            className="mt-auto w-full py-3 bg-white dark:bg-gray-800 text-primary-600 font-medium rounded-lg border border-primary-200 hover:bg-primary-50 transition-colors duration-300 flex items-center justify-center"
                          >
                            <Globe className="w-5 h-5 mr-2" />
                            Browse Library
                          </button>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow duration-300">
                        <div className="flex flex-col h-full">
                          <div className="bg-primary-50 text-primary-600 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                            <BookOpen className="w-6 h-6" />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">Documentation</h3>
                          <p className="text-gray-600 mb-6 flex-grow">Learn best practices and guidelines for assessments</p>
                          <button
                            className="mt-auto w-full py-3 bg-white dark:bg-gray-800 text-primary-600 font-medium rounded-lg border border-primary-200 hover:bg-primary-50 transition-colors duration-300 flex items-center justify-center"
                          >
                            <BookOpen className="w-5 h-5 mr-2" />
                            View Docs
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tiered Assessment Sections */}
                <div className="space-y-6">
                  
                  {/* === STUDY ASSESSMENTS === Primary Working Section */}
                  {showSection.study && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div 
                      className="px-5 py-4 bg-gradient-to-r from-primary-50 to-white dark:from-gray-800 dark:to-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between cursor-pointer hover:bg-primary-50/80 transition-colors"
                      onClick={() => setSectionsExpanded(prev => ({ ...prev, study: !prev.study }))}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary-100 rounded-lg">
                          <FlaskConical className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Study Assessments</h3>
                          <p className="text-sm text-gray-500">
                            {studyFilter != null
                              ? `${displayedStudyCount} of ${studyAssessments.length} assessments • filtered by study`
                              : `${studyAssessments.length} assessments • Your working documents`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Study picker — filter the section to a single study. Stop
                            propagation so interacting with it doesn't toggle the section. */}
                        {studyAssessmentsGrouped.length > 1 && (
                          <select
                            value={studyFilter == null ? '' : String(studyFilter)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              setStudyFilter(e.target.value === '' ? null : e.target.value);
                            }}
                            className="text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-400"
                          >
                            <option value="">All studies</option>
                            {studyAssessmentsGrouped.map((g) => (
                              <option key={g.study_id ?? '__unassigned__'} value={g.study_id == null ? '__unassigned__' : String(g.study_id)}>
                                {g.study_name} ({g.assessments.length})
                              </option>
                            ))}
                          </select>
                        )}
                        {sectionOpen.study ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </div>
                    </div>
                    
                    <AnimatePresence>
                      {sectionOpen.study && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="p-5"
                        >
                          {displayedStudyGroups.length > 0 ? (
                            <div className="space-y-6">
                              {displayedStudyGroups.map((group) => (
                                <div key={group.study_id || 'unassigned'} className="space-y-3">
                                  {/* Study Group Header */}
                                  <div className="flex items-center gap-2 pb-2 border-b border-gray-100 dark:border-gray-700">
                                    <FlaskConical className="w-4 h-4 text-primary-500" />
                                    <h4 className="text-sm font-semibold text-gray-700">{group.study_name}</h4>
                                    <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                                      {group.assessments.length} assessment{group.assessments.length !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                  {/* Study Group Assessments */}
                                  <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4' : 'flex flex-col gap-3'}>
                                    {group.assessments.map((assessment) => (
                                      <CleanAssessmentCard
                                        key={assessment.id}
                                        assessment={assessment}
                                        onEdit={handleContinueEditing}
                                        onManage={handleOpenManageModal}
                                        formatDate={formatDate}
                                        viewMode={viewMode}
                                        onReviewChanged={fetchAssessments}
                                        isApprover={canApprove}
                                        onReview={handleOpenReview}
                                      />
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : studyFilter != null ? (
                            <div className="text-center py-8">
                              <FlaskConical className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                              <p className="text-gray-500 mb-3">No assessments for the selected study.</p>
                              <button
                                onClick={() => setStudyFilter(null)}
                                className="text-sm font-semibold text-primary-600 hover:text-primary-700"
                              >
                                Show all studies
                              </button>
                            </div>
                          ) : (
                            <div className="text-center py-8">
                              <FlaskConical className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                              <p className="text-gray-500">No study assessments yet. Create one or copy from Sponsor Library.</p>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  )}

                  {/* === SPONSOR LIBRARY === Template Section */}
                  {showSection.sponsor && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-blue-200 overflow-hidden">
                    <div 
                      className="px-5 py-4 bg-gradient-to-r from-blue-50 to-white dark:from-gray-800 dark:to-gray-800 border-b border-blue-200 flex items-center justify-between cursor-pointer hover:bg-blue-50/80 transition-colors"
                      onClick={() => setSectionsExpanded(prev => ({ ...prev, sponsor: !prev.sponsor }))}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{getSponsorName()} Library</h3>
                          <p className="text-sm text-gray-500">{sponsorAssessments.length} templates • Organization templates</p>
                        </div>
                        <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">Templates</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {permissions.canEditSponsorLibrary && (
                          <span className="text-xs text-blue-600 font-medium">Can Edit</span>
                        )}
                        {sectionOpen.sponsor ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </div>
                    </div>
                    
                    <AnimatePresence>
                      {sectionOpen.sponsor && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="p-5"
                        >
                          {sponsorAssessments.length > 0 ? (
                            <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4' : 'flex flex-col gap-3'}>
                              {sponsorAssessments.map((assessment) => (
                                <CleanAssessmentCard
                                  key={assessment.id}
                                  assessment={assessment}
                                  onEdit={permissions.canEditSponsorLibrary ? handleContinueEditing : null}
                                  onManage={handleOpenManageModal}
                                  formatDate={formatDate}
                                  viewMode={viewMode}
                                  onReviewChanged={fetchAssessments}
                                  isApprover={canApprove}
                                  onReview={handleOpenReview}
                                />
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-8">
                              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                              <p className="text-gray-500">No sponsor templates yet.</p>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  )}

                  {/* === VPG LIBRARY === Global Templates */}
                  {showSection.vpg && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-indigo-200 overflow-hidden">
                    <div 
                      className="px-5 py-4 bg-gradient-to-r from-indigo-50 to-white dark:from-gray-800 dark:to-gray-800 border-b border-indigo-200 flex items-center justify-between cursor-pointer hover:bg-indigo-50/80 transition-colors"
                      onClick={() => setSectionsExpanded(prev => ({ ...prev, vpg: !prev.vpg }))}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          <Globe className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">VPG Global Library</h3>
                          <p className="text-sm text-gray-500">{filteredVpgAssessments.length} templates • Validated global instruments</p>
                        </div>
                        <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">Global</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Lock className="w-4 h-4 text-indigo-400" />
                        <span className="text-xs text-indigo-600 font-medium">Read-only</span>
                        {sectionOpen.vpg ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </div>
                    </div>
                    
                    <AnimatePresence>
                      {sectionOpen.vpg && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="p-5"
                        >
                          {loadingVpg ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader className="w-8 h-8 text-indigo-500 animate-spin" />
                            </div>
                          ) : filteredVpgAssessments.length > 0 ? (
                            <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4' : 'flex flex-col gap-3'}>
                              {filteredVpgAssessments.map((assessment) => (
                                <CleanAssessmentCard
                                  key={assessment.id}
                                  assessment={assessment}
                                  onImport={() => {
                                    setImportContext({ source: 'vpg', target: 'sponsor', assessment });
                                    setImportModalOpen(true);
                                  }}
                                  onManageVPG={(assessment) => {
                                    setSelectedVpgAssessment(assessment);
                                    setVpgManagementModalOpen(true);
                                  }}
                                  onPreview={handlePreviewAssessment}
                                  onEdit={user?.is_superuser ? handleContinueEditing : null}
                                  onDelete={user?.is_superuser ? (assessment) => {
                                    setDeleteConfirmation(assessment);
                                  } : null}
                                  formatDate={formatDate}
                                  viewMode={viewMode}
                                  isVPGLibrary={true}
                                  isSuperuser={user?.is_superuser}
                                />
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-8">
                              <Globe className="w-12 h-12 text-indigo-200 mx-auto mb-3" />
                              <p className="text-gray-500">No VPG templates available matching your search.</p>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  )}

                  {/* Archived Assessments Section */}
                  {showSection.archived && (
                  <ArchivedAssessments
                    isExpanded={sectionOpen.archived}
                    onToggle={() => setSectionsExpanded(prev => ({ ...prev, archived: !prev.archived }))}
                    onRefreshLibrary={fetchAssessments}
                    asSection={true}
                    refreshTrigger={archivedRefreshTrigger}
                  />
                  )}

                </div>
              </>
            )}
            
          </div>
        </motion.div>
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirmation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-[60] p-4"
            onClick={() => !deleting && setDeleteConfirmation(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-gray-900/20 max-w-md w-full mx-4 p-6"
            >
              <div className="flex items-start space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Archive Assessment</h3>
                  <p className="text-sm text-gray-600">
                    Are you sure you want to archive <strong>"{deleteConfirmation.name || deleteConfirmation.survey?.name || 'this assessment'}"</strong>?
                  </p>
                  <p className="text-sm text-amber-600 mt-2">
                    The assessment will be moved to the Archive and permanently deleted after 90 days. You can restore it from the Archive section.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setDeleteConfirmation(null)}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteAssessment(deleteConfirmation)}
                  disabled={deleting}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Archiving...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Archive
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Variant Selector Modal */}
      <VariantSelectorModal
        isOpen={variantModalOpen}
        onClose={handleCloseVariantModal}
        assessment={selectedAssessmentForVariants}
        onSelectVariant={handleSelectVariant}
        onCreateVariant={handleCreateVariant}
      />
      
      {/* Variant Creation Modal */}
      <PrimaryVariantModal
        isOpen={variantCreationModalOpen}
        onClose={() => {
          setVariantCreationModalOpen(false);
          setVariantCreationContext(null);
        }}
        onCreateVariant={handleCreateVariantSubmit}
        existingPrimaries={assessments.filter(a => !a.variant_info)}
        preselectedPrimary={variantCreationContext?.primaryAssessment}
        preselectedDeviceId={variantCreationContext?.preselectedDeviceId}
      />
      
      {/* Manage Assessment Modal */}
      {manageModalOpen && selectedAssessmentForManage && (
        <ManageAssessment
          assessment={selectedAssessmentForManage}
          onClose={handleCloseManageModal}
          onEdit={handleContinueEditing}
          onView={handleViewReadOnly}
          onCreateVariant={handleCreateVariant}
          onDuplicate={handleDuplicateAssessment}
          onDelete={setDeleteConfirmation}
          onRefresh={fetchAssessments}
          onUpdate={(updatedAssessment) => {
            setSelectedAssessmentForManage(updatedAssessment);
            fetchAssessments(); // Also refresh the list
          }}
          autoFocusRename={shouldAutoFocusRename}
        />
      )}
      
      {/* Import Assessment Utility Modal */}
      <AnimatePresence>
        {importModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => { setImportModalOpen(false); setImportContext(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/20 max-w-4xl w-full max-h-[85vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="px-6 py-4 bg-primary-600 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/10 rounded-lg">
                    <Download className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-white">Copy from Library</h2>
                </div>
                <button
                  onClick={() => { setImportModalOpen(false); setImportContext(null); }}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)]">
                {/* Step 1: Choose Import Flow (if no context set) */}
                {!importContext && (
                  <>
                    <p className="text-gray-600 mb-6">Choose where you want to copy from and where it should go:</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      {/* VPG to Sponsor Flow */}
                      <button
                        onClick={() => setImportContext({ source: 'vpg', target: 'sponsor', assessment: null })}
                        className="p-5 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-indigo-400 hover:bg-indigo-50/50 transition-all text-left group"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2.5 bg-indigo-100 rounded-lg group-hover:bg-indigo-200 transition-colors">
                            <Globe className="w-5 h-5 text-indigo-600" />
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-300" />
                          <div className="p-2.5 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                            <Building2 className="w-5 h-5 text-blue-600" />
                          </div>
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1">VPG → {getSponsorName()}</h3>
                        <p className="text-xs text-gray-500">Copy a global template to your organization</p>
                      </button>
                      
                      {/* VPG to Study Flow */}
                      <button
                        onClick={() => setImportContext({ source: 'vpg', target: 'study', assessment: null })}
                        className="p-5 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-violet-400 hover:bg-violet-50/50 transition-all text-left group"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2.5 bg-indigo-100 rounded-lg group-hover:bg-indigo-200 transition-colors">
                            <Globe className="w-5 h-5 text-indigo-600" />
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-300" />
                          <div className="p-2.5 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors">
                            <FlaskConical className="w-5 h-5 text-primary-600" />
                          </div>
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1">VPG → Study</h3>
                        <p className="text-xs text-gray-500">Copy a global template directly to your study</p>
                      </button>
                      
                      {/* Sponsor to Study Flow */}
                      <button
                        onClick={() => setImportContext({ source: 'sponsor', target: 'study', assessment: null })}
                        className="p-5 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-primary-400 hover:bg-primary-50/50 transition-all text-left group"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2.5 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                            <Building2 className="w-5 h-5 text-blue-600" />
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-300" />
                          <div className="p-2.5 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors">
                            <FlaskConical className="w-5 h-5 text-primary-600" />
                          </div>
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1">{getSponsorName()} → Study</h3>
                        <p className="text-xs text-gray-500">Copy an organization template to your study</p>
                      </button>
                    </div>
                  </>
                )}
                
                {/* Step 2: Select Assessment */}
                {importContext && !importContext.assessment && (
                  <>
                    {/* Flow indicator */}
                    <div className="flex items-center gap-3 mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <button
                        onClick={() => setImportContext(null)}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                      >
                        <ChevronRight className="w-5 h-5 text-gray-400 rotate-180" />
                      </button>
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${importContext.source === 'vpg' ? 'bg-indigo-100' : 'bg-blue-100'}`}>
                          {importContext.source === 'vpg' ? <Globe className="w-5 h-5 text-indigo-600" /> : <Building2 className="w-5 h-5 text-blue-600" />}
                        </div>
                        <span className="font-medium text-gray-700">{importContext.source === 'vpg' ? 'VPG Library' : getSponsorName()}</span>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                        <div className={`p-2 rounded-lg ${importContext.target === 'sponsor' ? 'bg-blue-100' : 'bg-primary-100'}`}>
                          {importContext.target === 'sponsor' ? <Building2 className="w-5 h-5 text-blue-600" /> : <FlaskConical className="w-5 h-5 text-primary-600" />}
                        </div>
                        <span className="font-medium text-gray-700">{importContext.target === 'sponsor' ? getSponsorName() : 'Study'}</span>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-4">Select an assessment to copy:</p>

                    {/* Loading State */}
                    {importContext.source === 'sponsor' && loadingSponsorAssessmentsForImport && (
                      <div className="flex items-center justify-center py-8">
                        <Loader className="w-8 h-8 text-primary-500 animate-spin" />
                      </div>
                    )}

                    {/* Assessment Grid */}
                    {(importContext.source !== 'sponsor' || !loadingSponsorAssessmentsForImport) && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(importContext.source === 'vpg' ? vpgAssessments : sponsorAssessmentsForImport).map((assessment) => (
                          <button
                            key={assessment.id}
                            onClick={() => setImportContext({ ...importContext, assessment })}
                            className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-400 hover:shadow-md transition-all text-left"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className={`p-2 rounded-lg ${importContext.source === 'vpg' ? 'bg-indigo-50' : 'bg-blue-50'}`}>
                                <FileText className={`w-5 h-5 ${importContext.source === 'vpg' ? 'text-indigo-600' : 'text-blue-600'}`} />
                              </div>
                            </div>
                            <h4 className="font-semibold text-gray-900 mb-1 line-clamp-2">{assessment.name || 'Untitled'}</h4>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span>v{assessment.version || '1.0'}</span>
                              <span>•</span>
                              <span>{assessment.assessment_type === 'fixed_form' ? 'Fixed Form' : 'Item Bank'}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {(importContext.source === 'vpg' ? vpgAssessments : sponsorAssessmentsForImport).length === 0 && !loadingSponsorAssessmentsForImport && (
                      <div className="text-center py-12">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                          {importContext.source === 'vpg' ? <Globe className="w-8 h-8 text-gray-400" /> : <Building2 className="w-8 h-8 text-gray-400" />}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Assessments Available</h3>
                        <p className="text-gray-600">There are no assessments in this library to copy. Create one first, or start from scratch.</p>
                      </div>
                    )}
                  </>
                )}
                
                {/* Step 3: Select Study (only when target is 'study') */}
                {importContext && importContext.assessment && importContext.target === 'study' && !importContext.study && (
                  <>
                    {/* Flow indicator */}
                    <div className="flex items-center gap-3 mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <button
                        onClick={() => setImportContext({ ...importContext, assessment: null })}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                      >
                        <ChevronRight className="w-5 h-5 text-gray-400 rotate-180" />
                      </button>
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${importContext.source === 'vpg' ? 'bg-indigo-100' : 'bg-blue-100'}`}>
                          {importContext.source === 'vpg' ? <Globe className="w-5 h-5 text-indigo-600" /> : <Building2 className="w-5 h-5 text-blue-600" />}
                        </div>
                        <span className="font-medium text-gray-700">{importContext.source === 'vpg' ? 'VPG Library' : getSponsorName()}</span>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                        <div className="p-2 rounded-lg bg-primary-100">
                          <FlaskConical className="w-5 h-5 text-primary-600" />
                        </div>
                        <span className="font-medium text-gray-700">Select Study</span>
                      </div>
                    </div>
                    
                    {/* Selected Assessment Summary */}
                    <div className="border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-gray-500" />
                        <span className="font-medium text-gray-700">{importContext.assessment.name || 'Untitled Assessment'}</span>
                        <CheckCircle className="w-4 h-4 text-primary-500 ml-auto" />
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-4">Select which study to copy this assessment to:</p>
                    
                    {/* Study Selection Grid */}
                    {loadingStudies ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader className="w-8 h-8 text-primary-500 animate-spin" />
                      </div>
                    ) : availableStudies.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {availableStudies.map((study) => (
                          <button
                            key={study.id}
                            onClick={() => setImportContext({ ...importContext, study })}
                            className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-400 hover:shadow-md transition-all text-left"
                          >
                            <div className="flex items-start gap-3">
                              <div className="p-2 bg-primary-50 rounded-lg">
                                <FlaskConical className="w-5 h-5 text-primary-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-gray-900 truncate">{study.title || study.name}</h4>
                                <p className="text-xs text-gray-500 mt-1">{study.code || study.id}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <FlaskConical className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Studies Available</h3>
                        <p className="text-gray-600">You don't have access to any studies.</p>
                      </div>
                    )}
                  </>
                )}
                
                {/* Step 4: Confirm Copy */}
                {importContext && importContext.assessment && (importContext.target === 'sponsor' || importContext.study) && (
                  <>
                    {/* Flow indicator */}
                    <div className="flex items-center gap-3 mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <button
                        onClick={() => importContext.target === 'study' ? setImportContext({ ...importContext, study: null }) : setImportContext({ ...importContext, assessment: null })}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                      >
                        <ChevronRight className="w-5 h-5 text-gray-400 rotate-180" />
                      </button>
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${importContext.source === 'vpg' ? 'bg-indigo-100' : 'bg-blue-100'}`}>
                          {importContext.source === 'vpg' ? <Globe className="w-5 h-5 text-indigo-600" /> : <Building2 className="w-5 h-5 text-blue-600" />}
                        </div>
                        <span className="font-medium text-gray-700">{importContext.source === 'vpg' ? 'VPG Library' : getSponsorName()}</span>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                        <div className={`p-2 rounded-lg ${importContext.target === 'sponsor' ? 'bg-blue-100' : 'bg-primary-100'}`}>
                          {importContext.target === 'sponsor' ? <Building2 className="w-5 h-5 text-blue-600" /> : <FlaskConical className="w-5 h-5 text-primary-600" />}
                        </div>
                        <span className="font-medium text-gray-700">
                          {importContext.target === 'sponsor' ? getSponsorName() : importContext.study?.title || importContext.study?.name || 'Study'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Selected Assessment */}
                    <div className="border-2 border-primary-200 bg-primary-50/50 rounded-xl p-6 mb-6">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-xl ${importContext.source === 'vpg' ? 'bg-indigo-100' : 'bg-blue-100'}`}>
                          <FileText className={`w-6 h-6 ${importContext.source === 'vpg' ? 'text-indigo-600' : 'text-blue-600'}`} />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {importContext.assessment.name || 'Untitled Assessment'}
                          </h3>
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span>v{importContext.assessment.version || '1.0'}</span>
                            <span>•</span>
                            <span>{importContext.assessment.assessment_type === 'fixed_form' ? 'Fixed Form' : 'Item Bank'}</span>
                          </div>
                        </div>
                        <CheckCircle className="w-6 h-6 text-primary-500" />
                      </div>
                    </div>
                    
                    {/* Name Input */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assessment Name
                      </label>
                      <input
                        type="text"
                        value={importContext.customName ?? `${importContext.assessment.name} (Copy)`}
                        onChange={(e) => setImportContext({ ...importContext, customName: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900"
                        placeholder="Enter a name for the copied assessment"
                      />
                      <p className="mt-1.5 text-xs text-gray-500">You can rename the assessment to avoid conflicts with existing names.</p>
                    </div>

                    {/* Error Message */}
                    {importContext.error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <p className="text-sm text-red-700">
                          <strong>Error:</strong> {importContext.error}
                        </p>
                      </div>
                    )}

                    {/* Info Message */}
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
                      <p className="text-sm text-gray-600">
                        {importContext.target === 'sponsor'
                          ? `This will create a copy of the assessment in your ${getSponsorName()} Library. You can then customize it and copy to studies.`
                          : `This will create a copy of the assessment in "${importContext.study?.title || importContext.study?.name}". You can customize it as needed.`}
                      </p>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => importContext.target === 'study' ? setImportContext({ ...importContext, study: null }) : setImportContext({ ...importContext, assessment: null })}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        Back
                      </button>
                      <button
                        onClick={async () => {
                          setImportContext({ ...importContext, loading: true, error: null });
                          const result = await handleImportAssessment(
                            importContext.assessment, 
                            importContext.target,
                            importContext.study?.id || null,
                            importContext.customName ?? `${importContext.assessment.name} (Copy)`
                          );
                          if (result.success) {
                            setImportModalOpen(false);
                            setImportContext(null);
                          } else {
                            setImportContext({ ...importContext, loading: false, error: result.error });
                          }
                        }}
                        disabled={importContext.loading}
                        className={`inline-flex items-center px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 ${
                          importContext.target === 'sponsor' 
                            ? 'bg-blue-600 hover:bg-blue-700' 
                            : 'bg-primary-600 hover:bg-primary-700'
                        }`}
                      >
                        {importContext.loading ? (
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Copy to {importContext.target === 'sponsor' ? getSponsorName() : importContext.study?.title || importContext.study?.name || 'Study'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* VPG Management Modal (Superuser Only) */}
      {vpgManagementModalOpen && selectedVpgAssessment && (
        <VPGManagementModal
          assessment={selectedVpgAssessment}
          onClose={() => {
            setVpgManagementModalOpen(false);
            setSelectedVpgAssessment(null);
          }}
          onEdit={handleContinueEditing}
          onDelete={(assessment) => {
            setDeleteConfirmation(assessment);
            setVpgManagementModalOpen(false);
            setSelectedVpgAssessment(null);
          }}
          onCopyToLibrary={(assessment) => {
            setImportContext({ source: 'vpg', target: 'sponsor', assessment });
            setImportModalOpen(true);
            setVpgManagementModalOpen(false);
            setSelectedVpgAssessment(null);
          }}
          formatDate={formatDate}
        />
      )}

      {/* Published Assessment Warning Modal (Dashboard Level) */}
      <AnimatePresence>
        {publishedWarningModalOpen && pendingPublishedAssessment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setPublishedWarningModalOpen(false);
              setPendingPublishedAssessment(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/20 max-w-md w-full overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-amber-100 p-3 rounded-full">
                    <Lock className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Locked Assessment</h3>
                    <p className="text-sm text-gray-500">v{pendingPublishedAssessment.config.version} - Locked</p>
                  </div>
                </div>

                <p className="text-gray-600 mb-4">
                  <strong>{pendingPublishedAssessment.config.name}</strong> is {LOCKED_STATUS_LABELS[pendingPublishedAssessment.config.publication_status] || 'locked'} and cannot be edited directly.
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-blue-900 mb-2">To make changes:</h4>
                  <p className="text-sm text-blue-800">
                    Create a new draft version which will copy all content from this version.
                    The current version is preserved unchanged so any study still using it is unaffected.
                  </p>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Version Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newVersionName}
                    onChange={(e) => setNewVersionName(e.target.value)}
                    placeholder="Enter a unique name for the new version"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      newVersionName.trim().toLowerCase() === pendingPublishedAssessment.config.name?.toLowerCase()
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                    }`}
                  />
                  {newVersionName.trim().toLowerCase() === pendingPublishedAssessment.config.name?.toLowerCase() && (
                    <p className="mt-1 text-xs text-red-600">
                      Name must be different from the original to avoid conflicts.
                    </p>
                  )}
                  {newVersionName.trim() && newVersionName.trim().toLowerCase() !== pendingPublishedAssessment.config.name?.toLowerCase() && (
                    <p className="mt-1 text-xs text-green-600">
                      ✓ Name is unique
                    </p>
                  )}
                  {!newVersionName.trim() && (
                    <p className="mt-1 text-xs text-gray-500">
                      Enter a new name to differentiate this version from the original.
                    </p>
                  )}
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setPublishedWarningModalOpen(false);
                      setPendingPublishedAssessment(null);
                      setNewVersionName('');
                    }}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Go Back
                  </button>
                  <button
                    onClick={handleCreateNewVersionFromDashboard}
                    disabled={creatingNewVersion || !newVersionName.trim() || newVersionName.trim().toLowerCase() === pendingPublishedAssessment.config.name?.toLowerCase()}
                    className="flex-1 px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {creatingNewVersion ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Create New Version
                      </>
                    )}
                  </button>
                </div>

                {user?.is_superuser && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleOpenReadOnlyBypass}
                      className="w-full text-xs text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Superuser: Open Read-Only
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default AssessmentBuilderMain;